// Client for the AL IKHTAYAR trading AI analytics service.
// The browser talks ONLY to the normal Node backend (:4000) under /api/sefi/*;
// Node proxies to the local SEFI FastAPI and injects the internal API key.
// No AI_INTERNAL_API_KEY and no X-Api-Key ever live in the frontend.
import axios from '../utils/axios'; // shared instance: REST_API base + JWT cookie (withCredentials)

const OFFLINE_MSG = 'SEFI is offline. Please start the SEFI desktop application.';

// Node returns HTTP 200 { status: 0, message } when the SEFI desktop/FastAPI is
// not running. Turn that into a tagged error so the existing "SEFI not connected"
// UI path (isSefiDownError) handles it, same as a real connection failure.
const unwrap = (data) => {
  if (data && data.status === 0 && data.message) {
    const err = new Error(data.message || OFFLINE_MSG);
    err.sefiOffline = true;
    throw err;
  }
  return data;
};

// Kept for backward compatibility with SEFI Settings. The token is no longer
// sent anywhere (Node holds the real internal key); this only touches localStorage.
const TOKEN_KEY = 'sefi_api_token';

export const getAiToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
};

export const setAiToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable — ignore */
  }
};

/**
 * Ask a trading analytics question in plain English.
 * @returns {Promise<{answer:string, sql_matches:Array, query_analysis:object, success:boolean}>}
 */
export const askTradingQuestion = async (question, conversationHistory = [], facilityId,
  page, pageSize, source) => {
  const params = {};
  if (facilityId) params.facility_id = facilityId; // forwarded only when a facility is selected
  if (page) params.page = page;                    // server-side pagination (list results)
  if (pageSize) params.page_size = pageSize;
  // source='try_asking' -> backend runs the deterministic (no-LLM) chip path.
  const body = { question, conversation_history: conversationHistory };
  if (source) body.source = source;
  const { data } = await axios.post(
    '/api/sefi/ask',
    body,
    { params, timeout: 30000 }
  );
  return unwrap(data);
};

/** Deterministic "Try asking" context: one real entity name per type for the
 *  current facility (supplier/customer/product/expense_type/staff/commission_agent).
 *  Used to fill chip templates; missing types are dropped. No AI. */
export const getTryAskingContext = async (facilityId) => {
  const params = {};
  if (facilityId) params.facility_id = facilityId;
  try {
    const { data } = await axios.get('/api/sefi/try-asking-context', { params, timeout: 8000 });
    return data || {};
  } catch {
    return {}; // on failure -> no dynamic chips, generics still shown
  }
};

/** Dashboard overview (KPIs + charts) for a date range: 30d|3m|6m|12m|thisyear,
 *  scoped to a facility (matches the app's per-facility order views). */
export const getOverview = async (range = '12m', facilityId) => {
  const params = { range };
  if (facilityId) params.facility_id = facilityId;
  const { data } = await axios.get('/api/sefi/analytics/overview', { params, timeout: 20000 });
  return unwrap(data);
};

/** Sales Insights (KPIs + 5 charts) for a range + facility. One request. */
export const getSalesInsights = async (range = '12m', facilityId) => {
  const params = { range };
  if (facilityId) params.facility_id = facilityId;
  const { data } = await axios.get('/api/sefi/analytics/sales', { params, timeout: 20000 });
  return unwrap(data);
};

/** Inventory Insights (KPIs + 6 datasets) for a range + facility. One request. */
export const getInventoryInsights = async (range = '12m', facilityId) => {
  const params = { range };
  if (facilityId) params.facility_id = facilityId;
  const { data } = await axios.get('/api/sefi/analytics/inventory', { params, timeout: 25000 });
  return unwrap(data);
};

/** Ping the AI service health endpoint (via Node). */
export const getAiHealth = async () => {
  const { data } = await axios.get('/api/sefi/health', { timeout: 8000 });
  return unwrap(data);
};

/**
 * True when an error means SEFI is unreachable — either Node flagged the upstream
 * as offline (status:0 → err.sefiOffline), or the Node API itself failed to answer
 * (connection refused / network error / timeout). Used to show the
 * "SEFI is not connected" state instead of raw technical messages.
 */
export const isSefiDownError = (e) => {
  if (!e) return false;
  if (e.sefiOffline) return true; // Node said the SEFI desktop app is offline
  if (e.response) return false;   // a running service answered (even with 4xx/5xx)
  const code = e.code || '';
  if (['ERR_NETWORK', 'ECONNABORTED', 'ECONNREFUSED', 'ETIMEDOUT'].includes(code)) return true;
  if (e.request !== undefined) return true; // request sent, no response received
  return /network error|failed to fetch|timeout|connection refused/i.test(e.message || '');
};

/** SEFI activation/license details for the selected facility. */
export const getSefiActivation = async (facilityId) => {
  const { data } = await axios.get('/api/sefi/activation', {
    params: { facility_id: facilityId },
    timeout: 8000
  });
  return data;
};

/** Owner only: set number of activation days. */
export const setSefiActivationDays = async (facilityId, activationDays) => {
  const { data } = await axios.put('/api/sefi/activation/days', {
    facility_id: Number(facilityId),
    activation_days: Number(activationDays)
  });
  return data;
};

/** Owner only: approve / renew SEFI activation. */
export const approveSefiActivation = async (facilityId) => {
  const { data } = await axios.post('/api/sefi/activation/approve', {
    facility_id: Number(facilityId)
  });
  return data;
};

// ---- SEFI desktop device licensing (requests signed by utils/axios interceptor) ----

/** Desktop: register this device (pending until Owner approves). */
export const registerSefiDevice = async (facilityId) => {
  const info = await window.desktop.license.getDevice();
  const { data } = await axios.post('/api/sefi/devices/register', {
    public_key: info.publicKey,
    device_name: info.deviceName,
    platform: info.platform,
    app_version: info.appVersion,
    facility_id: facilityId ? Number(facilityId) : undefined
  });
  return data;
};

/** Desktop: license status of this device (404 code SEFI_DEVICE_NOT_REGISTERED if none). */
export const getMySefiDevice = async () => {
  const { data } = await axios.get('/api/sefi/devices/me', { timeout: 8000 });
  return data;
};

/** Owner: list desktop devices. */
export const listSefiDevices = async (facilityId) => {
  const params = {};
  if (facilityId) params.facility_id = facilityId;
  const { data } = await axios.get('/api/sefi/devices', { params, timeout: 10000 });
  return data.devices || [];
};

/** Owner: approve | renew | revoke | transfer a device license. */
export const approveSefiDevice = async (id, activationDays) =>
  (await axios.post(`/api/sefi/devices/${id}/approve`, { activation_days: Number(activationDays) })).data;
export const renewSefiDevice = async (id, activationDays) =>
  (await axios.post(`/api/sefi/devices/${id}/renew`, { activation_days: Number(activationDays) })).data;
export const revokeSefiDevice = async (id) => (await axios.post(`/api/sefi/devices/${id}/revoke`)).data;
export const transferSefiDevice = async (id, toDeviceId) =>
  (await axios.post(`/api/sefi/devices/${id}/transfer`, { to_device_id: Number(toDeviceId) })).data;

// ---- SEFI Tailscale enrollment (device-signed; auth keys never reach the renderer) ----
export const getMyTailscaleEnrollment = async () =>
  (await axios.get('/api/sefi/devices/tailscale/status', { timeout: 8000 })).data;
export const listTailscaleEnrollments = async () =>
  (await axios.get('/api/sefi/devices/tailscale', { timeout: 10000 })).data.enrollments || [];
export const approveTailscaleEnrollment = async (id) => (await axios.post(`/api/sefi/devices/tailscale/${id}/approve`)).data;
export const revokeTailscaleEnrollment = async (id) => (await axios.post(`/api/sefi/devices/tailscale/${id}/revoke`)).data;
